import clsx from "clsx";
import moment from "moment";
import { IoAlertOutline, IoClose } from "swash/Icon";

import { Time } from "@/components/Time";

import { HasExperimentalFeature } from "./User";
import { ArticleExposure, Exposure } from "./article/ArticleExposureSelect";
import { HourRange } from "./article/ArticlePublishTime";
import { useResolvePublicationSlots } from "./article/util/publicationSlots";

type IndicatorTooltipProps = {
  articleExposure: ArticleExposure;
  exposure: Exposure;
};

export const TooltipTitle = (props: IndicatorTooltipProps) => {
  const { articleExposure, exposure } = props;
  return (
    <div>
      <span className="font-bold">{exposure.label} </span>
      {articleExposure.suggested &&
        !articleExposure.fulfilled &&
        !articleExposure.publication &&
        !articleExposure.planning.date && <span>(suggéré)</span>}
    </div>
  );
};

const TooltipPublicationBody = (props: {
  articleExposure: IndicatorTooltipProps["articleExposure"];
}) => {
  if (
    !props.articleExposure.publication ||
    (!props.articleExposure.suggested && props.articleExposure.fulfilled)
  )
    return null;
  return (
    <>
      Suggéré pour l’édition du{" "}
      <Time date={props.articleExposure.publication.date} format="DD/MM/YYYY" />
    </>
  );
};

const TooltipPlanningBody = (props: {
  articleExposure: IndicatorTooltipProps["articleExposure"];
}) => {
  const { articleExposure } = props;
  const { startDate } = useResolvePublicationSlots({
    plannedDate: articleExposure.planning.date,
    range: articleExposure.planning.range,
  });

  if (
    !startDate ||
    (!props.articleExposure.suggested && props.articleExposure.fulfilled)
  )
    return null;

  return (
    <>
      Suggéré pour exposition <br />
      <div className="flex">
        le {moment(startDate).format("DD/MM/YYYY")}{" "}
        {articleExposure.planning.range === "hour" ? (
          <div>
            entre <HourRange date={startDate} literal />
          </div>
        ) : (
          <>à {moment(startDate).format("HH:mm")}</>
        )}
      </div>
    </>
  );
};

const ExposureFullfilledText = ({ fulfilledAt }: { fulfilledAt: string }) => {
  return (
    <>
      Exposé le <Time date={fulfilledAt} format="DD/MM/YYYY à HH:mm:ss" />
    </>
  );
};

const TooltipFullfilledBody = (props: {
  articleExposure: IndicatorTooltipProps["articleExposure"];
}) => {
  const {
    articleExposure: { fulfilledAt },
  } = props;
  if (!fulfilledAt) return null;
  return <ExposureFullfilledText fulfilledAt={fulfilledAt} />;
};

export const TooltipBody = (props: IndicatorTooltipProps) => {
  const { articleExposure } = props;

  return (
    <>
      <TooltipPublicationBody articleExposure={articleExposure} />
      <TooltipPlanningBody articleExposure={articleExposure} />
      <TooltipFullfilledBody articleExposure={articleExposure} />
      <ShouldDisplayIndicators articleExposure={articleExposure}>
        <PriorArticlePublicationIndicatorTooltipContent {...props} />
        <DelayedVsArticlePublicationIndicatorTooltipContent {...props} />
      </ShouldDisplayIndicators>
      {articleExposure.fulfilledAt && articleExposure.suggested ? (
        <div className="mt-4">
          <strong>États précédents :</strong>
          <div>
            <ExposureFullfilledText fulfilledAt={articleExposure.fulfilledAt} />
          </div>
        </div>
      ) : null}
    </>
  );
};

export const ExposureIndicatorTooltip = (props: IndicatorTooltipProps) => {
  return (
    <div className="flex flex-col">
      <TooltipTitle {...props} />
      <TooltipBody {...props} />
    </div>
  );
};

const useShouldDisplayDelayedVsArticlePublicationIndicator = (
  props: IndicatorTooltipProps,
) => {
  const { articleExposure, exposure } = props;

  const { endDate } = useResolvePublicationSlots({
    plannedDate: articleExposure.planning?.date,
    range: articleExposure.planning?.range,
  });

  if (
    articleExposure.fulfilled ||
    !exposure?.delayedVsArticlePublication ||
    !endDate
  )
    return false;

  return moment
    .tz(articleExposure.article.schedulerDate, "Europe/Paris")
    .isAfter(moment.tz(endDate, "Europe/Paris"));
};

export const ShouldDisplayDelayedVsArticlePublicationIndicator = (
  props: IndicatorTooltipProps & { children: React.ReactNode },
) => {
  const { articleExposure, exposure, children } = props;
  if (
    !useShouldDisplayDelayedVsArticlePublicationIndicator({
      articleExposure,
      exposure,
    })
  )
    return null;
  return (
    // @ts-expect-error use a js component
    <HasExperimentalFeature feature="new-exposures-form">
      {children}
    </HasExperimentalFeature>
  );
};

const useShouldDisplayPriorArticlePublicationIndicator = (
  props: IndicatorTooltipProps,
) => {
  const { articleExposure, exposure } = props;
  const { startDate } = useResolvePublicationSlots({
    plannedDate: articleExposure.planning?.date,
    range: articleExposure.planning?.range,
  });
  const { article } = articleExposure;

  if (
    !article?.schedulerDate ||
    !exposure?.requiredPriorArticlePublication ||
    !startDate
  )
    return false;

  const { schedulerDate } = article;

  return moment(schedulerDate).isBefore(moment(startDate));
};

export const ShouldDisplayPriorArticlePublicationIndicator = (
  props: IndicatorTooltipProps & { children: React.ReactNode },
) => {
  const { articleExposure, exposure, children } = props;
  if (
    !useShouldDisplayPriorArticlePublicationIndicator({
      articleExposure,
      exposure,
    })
  )
    return null;
  return (
    // @ts-expect-error use a js component
    <HasExperimentalFeature feature="new-exposures-form">
      {children}
    </HasExperimentalFeature>
  );
};

export const DisplayPriorArticlePublicationIndicator = (props: {
  className: string;
}) => {
  return (
    <div
      className={clsx(
        "rounded-full bg-warning-bg-strong ring-white",
        props.className,
      )}
    >
      <IoAlertOutline
        className="text-white"
        aria-label="Date d’exposition postérieure à la publication"
      />
    </div>
  );
};

export const DelayedVsArticlePublicationIndicator = (props: {
  className: string;
}) => {
  return (
    <div
      className={clsx(
        "rounded-full bg-danger-bg-strong ring-white",
        props.className,
      )}
    >
      <IoClose
        className="text-white"
        aria-label="Date d’exposition antérieure à la publication"
      />
    </div>
  );
};

export const PriorArticlePublicationIndicatorTooltipContent = (
  props: IndicatorTooltipProps,
) => {
  const { articleExposure, exposure } = props;
  return (
    <ShouldDisplayPriorArticlePublicationIndicator
      articleExposure={articleExposure}
      exposure={exposure}
    >
      <div className="mt-2 flex items-start gap-2">
        <DisplayPriorArticlePublicationIndicator className="mt-1 text-base" />
        <span className="font-semibold text-white">
          Les articles sont plus lus lorsqu’ils sont exposés dans le même
          créneau horaire que leur publication
        </span>
      </div>
    </ShouldDisplayPriorArticlePublicationIndicator>
  );
};

export const DelayedVsArticlePublicationIndicatorTooltipContent = (
  props: IndicatorTooltipProps,
) => {
  const { articleExposure, exposure } = props;
  return (
    <ShouldDisplayDelayedVsArticlePublicationIndicator
      articleExposure={articleExposure}
      exposure={exposure}
    >
      <div className="mt-2 flex items-start gap-2">
        <DelayedVsArticlePublicationIndicator className="mt-1 text-base" />
        <span className="font-semibold text-white">
          L’heure d’exposition suggérée ne peut pas précéder la publication
        </span>
      </div>
    </ShouldDisplayDelayedVsArticlePublicationIndicator>
  );
};

export const ShouldDisplayIndicators = (props: {
  articleExposure: ArticleExposure;
  children: React.ReactNode;
}) => {
  const { articleExposure, children } = props;
  if (
    articleExposure.article.initialFirstPublished ||
    !articleExposure.article.schedulerDate
  )
    return null;
  return <>{children}</>;
};
